import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import React, { useEffect, useState } from "react";
import { ENUM_ROUTES } from "../../RoutesEnum";
import { showError } from "../../constants/toasts";
import { useLazyGetVenueDetailsQuery } from "../../services/venue";
import { VenueOwnerDetailsResponse } from "../../types/General";
import Description from "../../components/Description";

const VenueOwnerDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");
  const isVenue = type === "Venue";
  const [loading, setLoading] = useState(false);
  const [venueData, setVenueData] = useState<VenueOwnerDetailsResponse>();
  const [venueDetails] = useLazyGetVenueDetailsQuery();

  const getDataByIdList = async () => {
    try {
      setLoading(true);
      if (!id) return;
      const response = await venueDetails({ id }).unwrap();
      if (response.statusCode === 200) {
        setVenueData(response.data);
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      showError(error?.message || error?.data?.message);
    }
  };

  useEffect(() => {
    if (id) {
      getDataByIdList();
    }
  }, [id]);

  // Format date function
  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  // Format status function
  const formatStatus = (status: string) => {
    return status || "-";
  };

  // Format boolean function
  const formatBoolean = (value: boolean) => {
    return value ? "Yes" : "No";
  };

  // Format pricing function
  const formatPricing = (pricing: any) => {
    if (!pricing) return "-";
    return `$${pricing.perHour}/hour, $${pricing.perDay}/day, $${pricing.perPerson}/person`;
  };

  // Format facilities function
  const formatFacilities = (facilities: any[]) => {
    if (!facilities || facilities.length === 0) return "No facilities";
    return facilities.map((facility) => facility.name).join(", ");
  };

  if (loading) {
    return (
      <MainContainer>
        <div className="main_title">
          <h1>{`${type} Details`}</h1>
          <p>
            <span onClick={() => navigate("/dashboard")}>Dashboard</span> -{" "}
            <span onClick={() => navigate(ENUM_ROUTES.MANAGE_VENUE_OWNERS)}>
              {`Manage ${type}`}
            </span>{" "}
            -
          </p>
        </div>
        <div className="cards view_page">
          <div className="section_title">
            <h2>Loading...</h2>
          </div>
        </div>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <div className="main_title">
        <h1>{`${type} Details`}</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> -{" "}
          <span onClick={() => navigate(ENUM_ROUTES.MANAGE_VENUE_OWNERS)}>
            {`Manage ${type}`}
          </span>{" "}
          - {`${type} Details`}
        </p>
      </div>

      <div className="cards view_page">
        {/* <div className="section_title">
          <h2>{`${type} Information`}</h2>
        </div> */}

        {/* Venue Images Section */}
        <div className="gap_p">
          <div className="vew_img w_100">
            <h6
              style={{
                marginBottom: "15px",
                color: "#333",
                fontSize: "16px",
                fontWeight: "600",
              }}
            >
              {`${type} Images`}
            </h6>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                gap: "20px",
                marginBottom: "30px",
              }}
            >
              {venueData?.images && venueData.images.length > 0 ? (
                venueData.images.map((image, index) => (
                  <div
                    key={index}
                    style={{
                      borderRadius: "12px",
                      overflow: "hidden",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      transition: "transform 0.2s ease",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "scale(1.02)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                  >
                    <img
                      alt={`venue-${index + 1}`}
                      src={image || "/static/images/placeholder.png"}
                      style={{
                        width: "100%",
                        height: "200px",
                        objectFit: "cover",
                        display: "block",
                      }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = "/static/images/placeholder.png";
                      }}
                    />
                  </div>
                ))
              ) : (
                <div
                  style={{
                    borderRadius: "12px",
                    overflow: "hidden",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    width: "250px",
                    height: "200px",
                  }}
                >
                  <img
                    alt="venue"
                    src="/static/images/placeholder.png"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Basic Information Section */}
        <div className="gap_p" style={{ marginTop: "20px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "15px",
              marginBottom: "30px",
            }}
          >
            <div className="w_40">
              <h6>{`${type}`} Name</h6>
              <p>{venueData?.name || "-"}</p>
            </div>

            {/* <div className="w_33">
              <h6>{`${type}`} Type</h6>
              <p>{venueData?.type || "-"}</p>
            </div> */}

            <div className="w_33">
              <h6>Status</h6>
              <p>{formatStatus(venueData?.status || "")}</p>
            </div>

            <div className="w_33">
              <h6>Category</h6>
              <p>{venueData?.categoryId?.name || "-"}</p>
            </div>

            <div className="w_70">
              <h6>Location</h6>
              <p>{venueData?.address || "-"}</p>
            </div>

            <div className="w_40">
              <h6>Average Rating</h6>
              <p>
                {venueData?.averageRating
                  ? `${venueData.averageRating}/5`
                  : "-"}
              </p>
            </div>

            <div className="w_40">
              <h6>{isVenue ? "Guest Allowed" : "Capacity (Sq. Ft.)"}</h6>
              <p>
                {isVenue
                  ? venueData?.guestAllowed || "-"
                  : venueData?.capacity || "-"}
              </p>
            </div>

            <div className="w_40">
              <h6>Is Recommended</h6>
              <p>{formatBoolean(venueData?.isRecommended || false)}</p>
            </div>

            <div className="w_40">
              <h6>Is Favourite</h6>
              <p>{formatBoolean(venueData?.isFavourite || false)}</p>
            </div>

            <div className="w_40">
              <h6>Visibility Toggle</h6>
              <p>{formatBoolean(venueData?.visibilityTogle || false)}</p>
            </div>
          </div>
        </div>

        {/* Pricing Information Section */}
        <div className="gap_p" style={{ marginTop: "20px" }}>
          <h6>Pricing Information</h6>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            }}
          >
            <div className="w_40">
              <h6>Per {`${isVenue ? "Person" : "Month"}`}</h6>
              <p>
                $
                {isVenue
                  ? venueData?.setPricing?.perPerson || "-"
                  : venueData?.setPricing?.perMonth || "-"}
              </p>
            </div>

            <div className="w_40">
              <h6>Per Day</h6>
              <p>${venueData?.setPricing?.perDay || "-"}</p>
            </div>

            <div className="w_40">
              <h6>Per Hour</h6>
              <p>${venueData?.setPricing?.perHour || "-"}</p>
            </div>
            <div className="w_40">
              <h6>Security Fee</h6>
              <p>${venueData?.securityFee || "-"}</p>
            </div>
            {/* <div className="w_40">
              <h6>Per Month</h6>
              <p>${venueData?.setPricing?.perMonth || "-"}</p>
            </div> */}
          </div>
        </div>

        {/* Fees Section */}
        <div className="gap_p" style={{ marginTop: "20px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: "15px",
              marginBottom: "30px",
            }}
          >
            <div className="w_40">
              <h6>Cleaning Fee</h6>
              <p>${venueData?.cleaningFee || "-"}</p>
            </div>

            <div className="w_40">
              <h6>Insurance Fee</h6>
              <p>${venueData?.insuranceFee || "-"}</p>
            </div>

            <div className="w_40">
              <h6>{isVenue ? "Catering Fee" : "Packing Assistance"}</h6>
              <p>
                $
                {isVenue
                  ? venueData?.cateringFee || "-"
                  : venueData?.packingAssistanceFee || "-"}
              </p>
            </div>

            <div className="w_40">
              <h6>{isVenue ? "Decoration Fee" : "Transportation"}</h6>
              <p>
                $
                {isVenue
                  ? venueData?.decorationFee || "-"
                  : venueData?.transportationFee || "-"}
              </p>
            </div>
          </div>
        </div>

        {/* Description and Details Section */}
        <div className="gap_p" style={{ marginTop: "20px" }}>
          <Description
            heading="Description"
            text={venueData?.description}
            limit={200}
          />

          <Description
            heading="About Venue"
            text={venueData?.aboutVenue}
            limit={200}
          />

          <Description
            heading="Resources Included"
            text={venueData?.resourcesIncluded}
            limit={200}
          />

          <Description
            heading="Safety Parameters"
            text={venueData?.safetyParameters}
            limit={200}
          />

          <Description
            heading="Cancellation Policy"
            text={venueData?.cancellationPolicy}
            limit={200}
          />

          <Description
            heading="Other Policy"
            text={venueData?.otherPolicy}
            limit={200}
          />

          <div className="w_100">
            <h6>Facilities</h6>
            <p>{formatFacilities(venueData?.facilityId || [])}</p>
          </div>
        </div>
      </div>
    </MainContainer>
  );
};

export default VenueOwnerDetails;
